import { 
  GET_JOB_PROFILE_DETAILS_URL, 
  UPDATE_JOB_PROFILE_DETAILS_URL,
  JOB_META_URL,
  JOB_PURPOSE_URL,
  JOB_FUNCTIONS_URL,
  JOB_RESPONSIBILITIES_URL,
  JOB_OUTPUTS_URL,
  JOB_COMPETENCIES_URL,
  JOB_NOTICES_URL,
  JOB_PUBLISH_URL,
} from '@/utils/constants/constants.js';
import { parseJobDescriptionText } from '@/utils/job_parser_utils';
import axios from 'axios';

export default {
  methods: {
    // Mode and section setters
    set_parsed_editor_mode(mode) {
      this.parsed_editor_mode = mode;
    },
    
    set_parsed_section(section_key) {
      this.parsed_active_section = section_key;
    },

    // Undo/Redo
    undo_parsed_change() {
      if (this.parsed_history_index <= 0) return;
      this.parsed_history_index -= 1;
      const snapshot = Reflect.get(this.parsed_history, this.parsed_history_index);
      if (snapshot) this.parsed_document = JSON.parse(snapshot);
    },

    redo_parsed_change() {
      if (this.parsed_history_index >= this.parsed_history.length - 1) return;
      this.parsed_history_index += 1;
      const snapshot = Reflect.get(this.parsed_history, this.parsed_history_index);
      if (snapshot) this.parsed_document = JSON.parse(snapshot);
    },

    // Section content check
    section_has_content(section_key) {
      if (!this.parsed_document) return false;
      const doc = this.parsed_document;
      if (section_key === 'meta') return Object.values(doc.meta || {}).some((v) => v);
      if (section_key === 'job_purpose') return !!doc.job_purpose;
      if (section_key === 'main_functions') return Array.isArray(doc.main_functions) && doc.main_functions.length > 0;
      if (section_key === 'responsibilities') return Array.isArray(doc.responsibilities) && doc.responsibilities.length > 0;
      if (section_key === 'outputs_profile') return Array.isArray(doc.outputs_profile) && doc.outputs_profile.length > 0;
      if (section_key === 'competencies') {
        const comp = doc.competencies || {};
        return Object.values(comp).some((v) => Array.isArray(v) ? v.length > 0 : !!v);
      }
      if (section_key === 'career_pathing') return !!doc.career_pathing;
      if (section_key === 'amendments') return !!doc.amendments;
      if (section_key === 'performance_agreement') return !!doc.performance_agreement;
      if (section_key === 'signatories') return Array.isArray(doc.signatories) && doc.signatories.length > 0;
      return false;
    },

    // Dirty state and autosave
    mark_parsed_dirty(section_key) {
      this.parsed_dirty_sections = { ...this.parsed_dirty_sections, [section_key]: true };
      this.parsed_save_state = 'dirty';
      this.push_parsed_history();
      this.schedule_parsed_autosave(section_key);
    },

    schedule_parsed_autosave(section_key) {
      if (this.parsed_autosave_timer) clearTimeout(this.parsed_autosave_timer);
      this.parsed_autosave_timer = setTimeout(() => this.save_parsed_section(section_key), 800);
    },

    // Payload builders
    sanitize_parsed_text(value) {
      if (value === null || value === undefined) return null;
      const trimmed = String(value).trim();
      return trimmed || null;
    },

    sanitize_parsed_list(values) {
      if (!Array.isArray(values)) return [];
      return values.map(item => this.sanitize_parsed_text(item)).filter(Boolean);
    },

    build_parsed_payload(section_key) {
      const doc = this.parsed_document || this.default_parsed_document();
      if (section_key === 'meta') {
        return {
          meta: {
            post_title: this.sanitize_parsed_text(doc.meta.post_title),
            core_code: this.sanitize_parsed_text(doc.meta.core_code),
            occupational_classification: this.sanitize_parsed_text(doc.meta.occupational_classification),
            post_salary_level: this.sanitize_parsed_text(doc.meta.post_salary_level),
            location: this.sanitize_parsed_text(doc.meta.location),
            branch_component: this.sanitize_parsed_text(doc.meta.branch_component),
            reports_to: this.sanitize_parsed_text(doc.meta.reports_to),
            incumbent_name: this.sanitize_parsed_text(doc.meta.incumbent_name),
          },
          organogram: {
            top: this.sanitize_parsed_text(doc.organogram?.top),
            current_role: this.sanitize_parsed_text(doc.organogram?.current_role),
            peers: this.sanitize_parsed_list(doc.organogram?.peers || []),
          },
        };
      }
      if (section_key === 'job_purpose') return { job_purpose: this.sanitize_parsed_text(doc.job_purpose) };
      if (section_key === 'main_functions') return { main_functions: this.sanitize_parsed_list(doc.main_functions) };
      if (section_key === 'responsibilities') {
        return {
          responsibilities: (doc.responsibilities || []).map(item => ({
            heading: this.sanitize_parsed_text(item.heading),
            activities: this.sanitize_parsed_list(item.activities || []),
          })),
        };
      }
      if (section_key === 'outputs_profile') {
        return {
          outputs_profile: (doc.outputs_profile || []).map(item => ({
            customer: this.sanitize_parsed_text(item.customer),
            expectations: this.sanitize_parsed_list(item.expectations || []),
          })),
        };
      }
      if (section_key === 'competencies') {
        return {
          competencies: {
            minimum_qualification: this.sanitize_parsed_text(doc.competencies?.minimum_qualification),
            professional_requirements: this.sanitize_parsed_text(doc.competencies?.professional_requirements),
            minimum_experience: this.sanitize_parsed_text(doc.competencies?.minimum_experience),
            knowledge: this.sanitize_parsed_list(doc.competencies?.knowledge || []),
            skills: this.sanitize_parsed_list(doc.competencies?.skills || []),
            personal_attributes: this.sanitize_parsed_list(doc.competencies?.personal_attributes || []),
          },
        };
      }
      if (section_key === 'career_pathing') return { career_pathing: this.sanitize_parsed_text(doc.career_pathing) };
      if (section_key === 'amendments') return { amendments: this.sanitize_parsed_text(doc.amendments) };
      if (section_key === 'performance_agreement') return { performance_agreement: this.sanitize_parsed_text(doc.performance_agreement) };
      if (section_key === 'signatories') {
        return {
          signatories: (doc.signatories || []).map(item => ({
            role: item.role,
            name: this.sanitize_parsed_text(item.name),
            signature: this.sanitize_parsed_text(item.signature),
            date: this.sanitize_parsed_text(item.date),
          })),
        };
      }
      return {};
    },

    parsed_section_endpoint(section_key) {
      const mapping = {
        meta: JOB_META_URL,
        job_purpose: JOB_PURPOSE_URL,
        main_functions: JOB_FUNCTIONS_URL,
        responsibilities: JOB_RESPONSIBILITIES_URL,
        outputs_profile: JOB_OUTPUTS_URL,
        competencies: JOB_COMPETENCIES_URL,
        career_pathing: JOB_NOTICES_URL,
        amendments: JOB_NOTICES_URL,
        performance_agreement: JOB_NOTICES_URL,
        signatories: JOB_NOTICES_URL,
      };
      return Reflect.get(mapping, section_key);
    },

    replace_job_id_in_url(url) {
      return url.replace('{job_id}', String(this.parsed_job_id));
    },

    // Save section
    async save_parsed_section(section_key) {
      if (!this.parsed_document || !this.parsed_job_id) return;
      const endpoint = this.parsed_section_endpoint(section_key);
      if (!endpoint) return;
      
      const payload = this.build_parsed_payload(section_key);
      this.parsed_save_state = 'saving';
      this.parsed_save_message = 'Saving...';
      
      const response = await axios.patch(
        this.replace_job_id_in_url(endpoint),
        payload,
        { headers: this.headers }
      ).catch(() => {
        this.parsed_save_state = 'error';
        this.parsed_save_message = 'Save failed';
        this.show_error('Save failed - retry');
        return null;
      });
      
      if (response) {
        this.parsed_save_state = 'saved';
        this.parsed_save_message = 'Saved';
        this.parsed_dirty_sections = { ...this.parsed_dirty_sections, [section_key]: false };
        if (this.parsed_save_timer) clearTimeout(this.parsed_save_timer);
        this.parsed_save_timer = setTimeout(() => {
          this.parsed_save_state = 'idle';
          this.parsed_save_message = '';
        }, 2000);
      }
    },

    // Publish job
    async publish_parsed_job() {
      if (!this.parsed_document || !this.parsed_job_id) return;
      
      const response = await axios.post(
        this.replace_job_id_in_url(JOB_PUBLISH_URL),
        {},
        { headers: this.headers }
      ).catch(() => {
        this.show_error('Publish failed.');
        return null;
      });
      
      if (response) {
        this.show_success('Job description published.');
      }
    },

    // Export functions
    export_parsed_html() {
      const content = this.$refs.parsedPreview || this.$refs.jobPreviewPrint;
      const target = Array.isArray(content) ? this.safe_array_get(content, 0) : content;
      if (!target) {
        this.show_error('Switch to preview before exporting.');
        return;
      }
      const title = this.parsed_document?.meta?.post_title || 'Job Description';
      const doc = document.implementation.createHTMLDocument(title);
      const styles = document.querySelectorAll('link[rel="stylesheet"], style');
      styles.forEach(node => doc.head.appendChild(node.cloneNode(true)));
      doc.body.className = 'job-preview-print';
      doc.body.appendChild(target.cloneNode(true));
      const html = new XMLSerializer().serializeToString(doc);
      const blob = new Blob([html], { type: 'text/html' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${title}.html`;
      link.click();
      URL.revokeObjectURL(link.href);
    },

    export_parsed_pdf() {
      const content = this.$refs.parsedPreview || this.$refs.jobPreviewPrint;
      const target = Array.isArray(content) ? this.safe_array_get(content, 0) : content;
      if (!target) {
        this.show_error('Switch to preview before exporting.');
        return;
      }
      const title = this.parsed_document?.meta?.post_title || 'Job Description';
      const popup = window.open('', '_blank', 'width=1200,height=900');
      if (!popup) {
        this.show_error('Popup blocked. Please allow popups to export PDF.');
        return;
      }
      popup.document.title = title;
      const styles = document.querySelectorAll('link[rel="stylesheet"], style');
      styles.forEach(node => popup.document.head.appendChild(node.cloneNode(true)));
      popup.document.body.className = 'job-preview-print';
      popup.document.body.appendChild(target.cloneNode(true));
      popup.onload = () => { popup.focus(); popup.print(); };
    },

    extract_job_profile_data(response) {
      // Format: { success, data: { job_title, description, sections } }
      const wrapper = response?.data || {};
      const payload = wrapper.data || wrapper;
      const parsed = null;
      const job_id = payload?.id || null;
      return { payload, parsed, job_id };
    },

    initialize_profile_view(entity_type_id, payload, parsed, job_id, row, initial_mode) {
      if (job_id && parsed) {
        this.init_parsed_editor(parsed, job_id);
      }
      
      this.view_job_entity_type_id = entity_type_id;
      this.view_job_title = payload.job_title || payload.title || this.safe_obj_get(row, 'Job Profile') || '';
      const normalized = this.normalize_sections(payload.sections || [], 'Section');
      this.view_job_sections = normalized;
      this.view_job_description = this.dedupe_global_description(payload.description || '', normalized);
      this.view_active_section_index = 0;
      this.view_mode = initial_mode;
    },

    async open_job_profile_modal(row, initial_mode = 'preview') {
      const entity_type_id = row?.id;
      if (!entity_type_id) {
        this.show_error('Entity type id is required.');
        return;
      }
      
      const response = await axios.get(GET_JOB_PROFILE_DETAILS_URL, {
        headers: this.headers,
        params: { entity_type_id },
      }).catch((err) => {
        this.show_error(this.extract_error_message(err));
        return null;
      });
      
      if (!response) return;
      
      const { payload, parsed: raw_parsed, job_id } = this.extract_job_profile_data(response);
      let parsed = raw_parsed;
      let rawText = null;
      
      if (!parsed && payload.sections && Array.isArray(payload.sections) && payload.sections.length > 0) {
        const first_section = this.safe_array_get(payload.sections, 0);
        const descriptions = first_section?.descriptions;
        rawText = Array.isArray(descriptions) ? this.safe_array_get(descriptions, 0) : null;
        if (rawText && typeof rawText === 'string' && rawText.length > 100) {
          parsed = parseJobDescriptionText(rawText);
        }
      }
      
      // Store raw text for fallback display
      this.parsed_raw_text = rawText;
      
      this.initialize_profile_view(entity_type_id, payload, parsed, job_id, row, initial_mode);
    },

    async open_job_profile_edit_modal(row) {
      await this.open_job_profile_modal(row, 'edit');
    },

    async save_job_profile_details() {
      if (!this.view_job_entity_type_id) {
        this.show_error('Entity type id is required.');
        return;
      }
      const document_payload = {
        id: this.view_job_entity_type_id,
        title: this.view_job_title,
        description: this.view_job_description,
        sections: this.view_job_sections,
      };
      const result = await axios.post(UPDATE_JOB_PROFILE_DETAILS_URL, {
        entity_type_id: this.view_job_entity_type_id,
        document: document_payload,
      }, { headers: this.headers }).catch((err) => {
        this.show_error(this.extract_error_message(err));
        return null;
      });
      if (result) this.show_success('Job profile details saved successfully.');
    },

    export_preview_pdf() {
      const preview_node = this.$refs.jobPreviewPrint;
      const target = Array.isArray(preview_node) ? this.safe_array_get(preview_node, 0) : preview_node;
      if (!target) {
        this.show_info('Switch to Styled Preview to export.');
        return;
      }
      const popup = window.open('', '_blank', 'width=1200,height=900');
      if (!popup) {
        this.show_error('Popup blocked. Please allow popups to export the PDF.');
        return;
      }
      const title = this.view_job_title || 'Job Profile';
      this.render_export_document(popup.document, title, target);
      popup.onload = () => { popup.focus(); popup.print(); };
    },

    default_parsed_document() {
      return {
        meta: {
          post_title: null,
          core_code: null,
          occupational_classification: null,
          post_salary_level: null,
          location: null,
          branch_component: null,
          reports_to: null,
          incumbent_name: null,
        },
        organogram: { top: null, peers: [], current_role: null },
        job_purpose: null,
        main_functions: [],
        responsibilities: [],
        outputs_profile: [],
        competencies: {
          minimum_qualification: null,
          professional_requirements: null,
          minimum_experience: null,
          knowledge: [],
          skills: [],
          personal_attributes: [],
        },
        career_pathing: null,
        amendments: null,
        performance_agreement: null,
        signatories: [
          { role: 'CD: Corporate Management', name: null, signature: null, date: null },
          { role: 'Supervisor/Manager', name: null, signature: null, date: null },
          { role: 'Incumbent', name: null, signature: null, date: null },
        ],
      };
    },

    normalize_parsed_document(parsed) {
      const base = this.default_parsed_document();
      const safe = parsed && typeof parsed === 'object' ? parsed : {};
      const merged = {
        ...base,
        ...safe,
        meta: { ...base.meta, ...(safe.meta || {}) },
        organogram: { ...base.organogram, ...(safe.organogram || {}) },
        competencies: { ...base.competencies, ...(safe.competencies || {}) },
      };
      if (!Array.isArray(merged.main_functions)) merged.main_functions = [];
      if (!Array.isArray(merged.responsibilities)) merged.responsibilities = [];
      if (!Array.isArray(merged.outputs_profile)) merged.outputs_profile = [];
      if (!Array.isArray(merged.organogram.peers)) merged.organogram.peers = [];
      merged.signatories = Array.isArray(merged.signatories) ? merged.signatories : base.signatories;
      return merged;
    },

    init_parsed_editor(parsed, job_id) {
      this.parsed_document = this.normalize_parsed_document(parsed || {});
      this.parsed_job_id = job_id || null;
      this.parsed_active_section = 'meta';
      this.parsed_responsibility_index = 0;
      this.parsed_dirty_sections = {};
      this.parsed_save_state = 'idle';
      this.parsed_save_message = '';
      this.parsed_history = [];
      this.parsed_history_index = -1;
      this.push_parsed_history();
    },

    push_parsed_history() {
      if (!this.parsed_document) return;
      const snapshot = JSON.stringify(this.parsed_document);
      const current_snapshot = Reflect.get(this.parsed_history, this.parsed_history_index);
      if (current_snapshot === snapshot) return;
      const trimmed = this.parsed_history.slice(0, this.parsed_history_index + 1);
      trimmed.push(snapshot);
      this.parsed_history = trimmed.slice(-50);
      this.parsed_history_index = this.parsed_history.length - 1;
    },
  },
};
